/**
 * The only place in the app that calls `fetch` (gym-app.md). Every call carries the opaque bearer token;
 * every POST carries an Idempotency-Key (one per user intent: pass your own to reuse it across retries).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null = null,
    /** Seconds from a `Retry-After` header, when the server sent one. */
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isForbidden() { return this.status === 403; }
  get isNotFound() { return this.status === 404; }
  get isOffline() { return this.status === 0; }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export interface RequestOptions {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface ClientDeps {
  baseUrl: () => string;
  getToken: () => string | null;
  /** Called on `401 invalid_session` / `unauthenticated`: clear the token and go to sign-in. */
  onUnauthenticated: () => void;
  /**
   * Kiosk mode (app-kiosk): `getToken` returns the kiosk credential, which goes to `/kiosk/*` and nowhere else.
   * Any other path throws before a request is made, so the kiosk token cannot reach a staff route by mistake.
   * A `401 invalid_kiosk` calls `onInvalidKiosk` instead of `onUnauthenticated`.
   */
  kiosk?: { onInvalidKiosk: () => void };
}

export interface ApiClient {
  request<T>(method: string, path: string, opts?: RequestOptions): Promise<T>;
  get<T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
}

export const newIdempotencyKey = () => globalThis.crypto.randomUUID();

const SESSION_CODES = new Set(["invalid_session", "unauthenticated"]);

function buildUrl(base: string, path: string, query?: RequestOptions["query"]) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return `${base.replace(/\/$/, "")}${path}${s ? `?${s}` : ""}`;
}

/** The only paths the kiosk token may be sent to. */
export const isKioskPath = (path: string) => path.startsWith("/kiosk/") && !path.includes("..") && !path.includes("?");

export function createClient(deps: ClientDeps): ApiClient {
  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    if (deps.kiosk && !isKioskPath(path)) throw new Error(`The kiosk client only calls /kiosk/* routes, not ${path}`);
    const headers: Record<string, string> = { accept: "application/json" };
    const token = deps.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    // A Blob (a file the user picked) goes as raw bytes with its own type; anything else is JSON.
    const raw = typeof Blob !== "undefined" && opts.body instanceof Blob;
    if (opts.body !== undefined) headers["content-type"] = raw ? (opts.body as Blob).type || "application/octet-stream" : "application/json";
    if (method === "POST") headers["idempotency-key"] = opts.idempotencyKey ?? newIdempotencyKey();

    let res: Response;
    try {
      res = await fetch(buildUrl(deps.baseUrl(), path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : raw ? await (opts.body as Blob).arrayBuffer() : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new ApiError(0, "offline", "Could not reach the server. Check your connection.");
    }

    const requestId = res.headers.get("x-request-id");
    if (!res.ok) {
      let code = "internal";
      let message = res.statusText || "Request failed";
      try {
        const body = (await res.json()) as { error?: { code?: string; message?: string } };
        if (body.error?.code) code = body.error.code;
        if (body.error?.message) message = body.error.message;
      } catch {
        /* not JSON: keep the defaults */
      }
      const retry = Number(res.headers.get("retry-after"));
      const err = new ApiError(res.status, code, message, requestId, Number.isFinite(retry) && retry > 0 ? retry : null);
      // A 401 for a token that has since been replaced (branch switch rotates it) says nothing about the current session.
      if (deps.kiosk) {
        if (res.status === 401 && code === "invalid_kiosk") deps.kiosk.onInvalidKiosk();
      } else if (res.status === 401 && SESSION_CODES.has(code) && deps.getToken() === token) deps.onUnauthenticated();
      throw err;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    request,
    get: (path, query, signal) => request("GET", path, { query, signal }),
    post: (path, body, opts) => request("POST", path, { ...opts, body }),
    patch: (path, body, opts) => request("PATCH", path, { ...opts, body }),
    del: (path, opts) => request("DELETE", path, opts),
  };
}

/** `apiBase()` from the native bridge when running in the Capacitor shell, else the Vite env var. */
export function apiBase(): string {
  const bridge = (globalThis as { HelmrNative?: { apiBase?: () => string } }).HelmrNative;
  return bridge?.apiBase?.() ?? import.meta.env.VITE_API_BASE ?? "";
}
